import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import authApi, { PlanResponse } from '../../services/authApi';
import './HomePage.css';


const COURSES_PREVIEW = [
  { icon: '📊', title: 'SwingTrading Foundations', badge: 'Free', desc: 'Support/resistance, trend structure, and how to read a daily chart.', students: '1,240', hours: '6h' },
  { icon: '🕯️', title: 'Chart Pattern Playbook', badge: 'Pro', desc: 'Bull flags, cup-and-handle, descending triangles — entry triggers and stop placement.', students: '890', hours: '9h' },
  { icon: '⚡', title: 'Momentum Trading with ChartInk', badge: 'Pro', desc: 'Build and refine ChartInk screeners for momentum setups.', students: '560', hours: '5.5h' },
  { icon: '📓', title: 'Trade Journal Workshop', badge: 'Free', desc: 'The one habit that separates improving traders from those who repeat mistakes.', students: '2,100', hours: '2.5h' },
];

const SCREENERS_PREVIEW = [
  { name: 'Breakout Candidates', count: '43 stocks', tags: ['Daily', 'Breakout', 'Volume'] },
  { name: 'Bull Flag Pattern', count: '28 stocks', tags: ['Weekly', 'Bull Flag'] },
  { name: 'Volume Surge Setup', count: '67 stocks', tags: ['Daily', 'Volume', 'Momentum'] },
  { name: 'RSI Pullback Entry', count: '19 stocks', tags: ['Daily', 'RSI', 'Pullback'] },
  { name: '52-Week Low Reversal', count: '11 stocks', tags: ['Weekly', 'Reversal'] },
  { name: 'EMA Crossover', count: '34 stocks', tags: ['Daily', 'EMA', 'Trend'] },
];

const STATS = [
  { val: '2,400+', label: 'Members' },
  { val: '14', label: 'Learning' },
  { val: '18', label: 'Screeners' },
  { val: '4.8★', label: 'Rated' },
];

const VALUES = [
  { icon: '📚', title: 'Structured Learning', desc: 'Step-by-step modules on price action, chart patterns, screener setup, and trade journaling. Learn at your pace with lifetime access.' },
  { icon: '📈', title: 'Tested Screener Links', desc: 'Curated screeners we use ourselves — pattern setups, breakout watchlists, volume surges. For learning and reference only.' },
  { icon: '🤝', title: 'Active Community', desc: 'Ask questions, share trade setups, get feedback from fellow members and mentors. Weekly market discussions and live Q&A sessions.' },
];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, googleLogin } = useAuth();
  const [signingIn, setSigningIn] = useState(false);
  const [plans, setPlans] = useState<PlanResponse[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    authApi.getPlans('COMMUNITY')
      .then((r) => setPlans(r.data || []))
      .catch(() => {})
      .finally(() => setPlansLoading(false));
  }, []);

  const handleGoogleSuccess = async (res: CredentialResponse) => {
    if (!res.credential) return;
    setSigningIn(true);
    try {
      await googleLogin(res.credential);
    } catch {
      toast.error('Google sign-in failed.');
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <div className="hp-wrap">
      {/* Hero */}
      <section className="hp-hero">
        <div className="hp-hero-inner">
          <div className="hp-eyebrow">
            <span className="hp-eyebrow-dot"></span>
            Live Community · 2,400+ Traders
          </div>
          <h1 className="hp-title">
            Learn to trade with a <em>system,</em><br />not a feeling.
          </h1>
          <p className="hp-sub">
            Real learning, curated screener links, and a community of traders who test their strategies before risking capital.
          </p>
          <div className="hp-ctas">
            {isAuthenticated ? (
              <button className="hp-btn-primary" onClick={() => navigate('/courses')}>
                Go to Learning →
              </button>
            ) : signingIn ? (
              <div className="hp-signing-in">Signing in…</div>
            ) : (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => toast.error('Google sign-in failed')}
                useOneTap={false}
                theme="filled_black"
                size="large"
                text="continue_with"
                shape="rectangular"
              />
            )}
            <button className="hp-btn-ghost" onClick={() => navigate('/courses')}>
              Browse Learning
            </button>
          </div>
          <div className="hp-stats">
            {STATS.map((s) => (
              <div key={s.label} className="hp-stat">
                <span className="hp-stat-val">{s.val}</span>
                <span className="hp-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="hp-section">
        <div className="hp-section-inner">
          <p className="hp-eyebrow-sm">Why members join</p>
          <h2 className="hp-section-title">Everything a self-taught trader needs</h2>
          <div className="hp-values">
            {VALUES.map((v) => (
              <div key={v.title} className="hp-value-card">
                <div className="hp-value-icon">{v.icon}</div>
                <h3 className="hp-value-title">{v.title}</h3>
                <p className="hp-value-desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning preview */}
      <div className="hp-alt-bg">
        <section className="hp-section">
          <div className="hp-section-inner">
            <p className="hp-eyebrow-sm">Learning</p>
            <h2 className="hp-section-title">Learn the method, then verify it.</h2>
            <div className="hp-courses">
              {COURSES_PREVIEW.map((c) => (
                <div key={c.title} className="hp-course-card" onClick={() => navigate('/courses')}>
                  <div className="hp-course-top">
                    <span className="hp-course-icon">{c.icon}</span>
                    <span className={`hp-badge ${c.badge === 'Free' ? 'badge-free' : 'badge-pro'}`}>{c.badge}</span>
                  </div>
                  <div className="hp-course-title">{c.title}</div>
                  <div className="hp-course-desc">{c.desc}</div>
                  <div className="hp-course-meta">
                    <span>⏱ {c.hours}</span>
                    <span>👥 {c.students}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="hp-view-all">
              <button className="hp-view-btn" onClick={() => navigate('/courses')}>View All Learning →</button>
            </div>
          </div>
        </section>
      </div>

      {/* Screeners preview */}
      <section className="hp-section">
        <div className="hp-section-inner">
          <p className="hp-eyebrow-sm">Curated Screeners</p>
          <h2 className="hp-section-title">Screener links we teach with.</h2>
          <div className="hp-screeners">
            {SCREENERS_PREVIEW.map((s) => (
              <div key={s.name} className="hp-screener-card" onClick={() => navigate('/screeners')}>
                <div className="hp-screener-head">
                  <span className="hp-screener-name">{s.name}</span>
                  <span className="hp-screener-count">{s.count}</span>
                </div>
                <div className="hp-screener-tags">
                  {s.tags.map((t) => <span key={t} className="hp-stag">{t}</span>)}
                </div>
              </div>
            ))}
          </div>

          <div className="hp-view-all">
            <button className="hp-view-btn" onClick={() => navigate('/screeners')}>View All Screeners →</button>
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <div className="hp-alt-bg">
        <section className="hp-section">
          <div className="hp-section-inner">
            <p className="hp-eyebrow-sm">Membership</p>
            <h2 className="hp-section-title">Simple plans, no surprises.</h2>

            {plansLoading ? (
              <div className="hp-plans-loading">
                <div className="hp-plans-dot" /><div className="hp-plans-dot" /><div className="hp-plans-dot" />
              </div>
            ) : plans.length === 0 ? (
              <div className="hp-plans-cta">
                <p className="hp-plans-cta-text">Transparent pricing with full access — courses, screeners, and community.</p>
                <button className="hp-plan-btn filled hp-plans-cta-btn" onClick={() => navigate('/pricing')}>
                  View Pricing →
                </button>
              </div>
            ) : (
              <>
                <div className="hp-plans">
                  {plans.map((plan, idx) => {
                    const isFirst = idx === 0;
                    const annual = plan.durationMonths >= 12;
                    const period = plan.durationMonths === 1 ? '/mo'
                      : plan.durationMonths === 3  ? '/3 mo'
                      : plan.durationMonths === 6  ? '/6mo'
                      : plan.durationMonths === 12 ? '/yr'
                      : `/${plan.durationMonths}mo`;
                    return (
                      <div key={plan.id} className={`hp-plan ${
                        plan.durationMonths === 6 ? 'popular-purple' :
                        isFirst && !annual ? 'popular' : ''
                      }`}>
                        {isFirst && !annual && <div className="hp-popular-badge">Most Popular</div>}
                        {annual && <div className="hp-popular-badge" style={{ background: 'var(--gold)' }}>Best Value</div>}
                        <div className="hp-plan-name">{plan.planName}</div>
                        <div className="hp-plan-price">
                          ₹{Number(plan.price).toLocaleString('en-IN')}
                          <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--muted)' }}>{period}</span>
                        </div>
                        {plan.originalPrice > plan.price && (
                          <div className="hp-savings-row">
                            <span className="hp-original-price">₹{Number(plan.originalPrice).toLocaleString('en-IN')}</span>
                            {plan.discountPercent > 0 && (
                              <span className="hp-discount-badge">{plan.discountPercent}% OFF</span>
                            )}
                          </div>
                        )}
                        <ul className="hp-plan-features">
                          {(plan.features || []).slice(0, 4).map((f) => (
                            <li key={f}>✓ {f.trim()}</li>
                          ))}
                        </ul>
                        <button
                          className={`hp-plan-btn ${plan.durationMonths === 6 ? 'purple' : annual ? 'gold' : 'filled'}`}
                          onClick={() => navigate('/pricing')}
                        >
                          Get Started
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="hp-view-all" style={{ marginTop: 20 }}>
                  <button className="hp-view-btn" onClick={() => navigate('/pricing')}>
                    See full pricing details →
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      {/* Disclaimer */}
      <div className="hp-disclaimer">
        <div className="hp-disclaimer-inner">
          ⚠️ SwingRadarX Academy is not a SEBI registered investment advisor. All content is strictly for educational and informational purposes only and does not constitute investment advice or buy/sell recommendations.
        </div>
      </div>
    </div>
  );
};

export default HomePage;
